// frontend/src/components/WebhookModal.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Input,
  Label,
  Modal,
  Select,
  Text,
  Alert,
} from '@nimbus-ds/components';
import { WEBHOOK_EVENTS } from '../constants/webhookEvents';
import type { Webhook, CreateWebhookData, UpdateWebhookData } from '../services/api';

interface WebhookModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateWebhookData | UpdateWebhookData) => Promise<void>;
  webhook?: Webhook | null;
  isLoading?: boolean;
}

export const WebhookModal: React.FC<WebhookModalProps> = ({
  isOpen,
  onClose,
  onSave,
  webhook,
  isLoading = false,
}) => {
  const [event, setEvent] = useState('');
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const isEditing = !!webhook;

  useEffect(() => {
    if (webhook) {
      setEvent(webhook.event);
      setUrl(webhook.url);
    } else {
      setEvent('');
      setUrl('');
    }
    setError(null);
  }, [webhook, isOpen]);

  const handleSubmit = async () => {
    setError(null);

    if (!event) {
      setError('Selecciona un evento');
      return;
    }

    if (!url) {
      setError('Ingresa una URL');
      return;
    }

    if (!url.startsWith('https://')) {
      setError('La URL debe comenzar con https://');
      return;
    }

    setSaving(true);
    try {
      await onSave({ event, url });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={isOpen} onDismiss={onClose}>
      <Modal.Header title={isEditing ? 'Editar Webhook' : 'Nuevo Webhook'} />
      <Modal.Body padding="base">
        <Box display="flex" flexDirection="column" gap="4">
          {error && (
            <Alert appearance="danger" title="Error">
              {error}
            </Alert>
          )}

          <Box display="flex" flexDirection="column" gap="1">
            <Label htmlFor="event">Evento</Label>
            <Select
              id="event"
              name="event"
              value={event}
              onChange={(e) => setEvent(e.target.value)}
              disabled={isEditing}
            >
              <Select.Option value="" label="Selecciona un evento" disabled>
                Selecciona un evento
              </Select.Option>
              {WEBHOOK_EVENTS.map((e) => (
                <Select.Option key={e.value} value={e.value} label={e.label}>
                  {e.label}
                </Select.Option>
              ))}
            </Select>
            {isEditing && (
              <Text fontSize="caption" color="neutral-textDisabled">
                El evento no puede modificarse. Elimina el webhook y crea uno nuevo.
              </Text>
            )}
          </Box>

          <Box display="flex" flexDirection="column" gap="1">
            <Label htmlFor="url">URL del Webhook (HTTPS)</Label>
            <Input
              id="url"
              placeholder="https://tu-dominio.com/webhook/..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            <Text fontSize="caption" color="neutral-textDisabled">
              Obtén esta URL desde n8n al crear un nodo Webhook.
            </Text>
          </Box>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Box display="flex" gap="2" justifyContent="flex-end">
          <Button appearance="transparent" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={saving || isLoading}>
            {saving ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Crear webhook'}
          </Button>
        </Box>
      </Modal.Footer>
    </Modal>
  );
};
